import { useEffect, useMemo, useRef, useState } from "react";

import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  ChevronDown,
  Database,
  Info,
  ListChecks,
  Loader2,
  Search,
  Sparkles,
  Target,
  TrendingUpDown,
} from "lucide-react";

import { Link, routes } from "@redwoodjs/router";

import { AssistantMessageContent } from "src/components/AssistantChat/AssistantMessageContent";
import { PromptSuggestions } from "src/components/AssistantChat/PromptSuggestions";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "src/components/ui/collapsible";
import { ScrollArea } from "src/components/ui/scroll-area";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "src/components/ui/tooltip";
import { getDashboardInsightMetricGlossary } from "src/lib/dashboardInsights";
import { cn } from "src/lib/utils";

const DEFAULT_PROGRESS_MESSAGE = "Working...";
const TYPEWRITER_INTERVAL_MS = 16;
const TYPEWRITER_CHARS_PER_TICK = 12;
const MAX_TYPEWRITER_CHARS = 1800;
const LINE_REVEAL_INTERVAL_MS = 42;
const MAX_LINE_REVEAL_CHARS = 3200;
const INSIGHT_ACTION_PLAN_KIND = "insight_action_plan";
const INSIGHT_ACTION_PLAN_RESPONSE_KIND = "insight_action_plan_response";
const INSPECT_TARGETS = {
  unit_economics: {
    label: "View unit economics",
    routeName: "analytics",
    routeParams: { child: "unitEconomics" },
  },
  cohort_analysis: {
    label: "View cohorts",
    routeName: "analytics",
    routeParams: { child: "cohortAnalytics" },
  },
  marketing: {
    label: "View marketing",
    routeName: "marketing",
    routeParams: { child: "marketingActivity" },
  },
  profit_loss: {
    label: "View P&L",
    routeName: "finance",
    routeParams: { child: "normalizedPL" },
  },
  aov_analytics: {
    label: "View AOV analytics",
    routeName: "analytics",
    routeParams: { child: "aovAnalytics" },
  },
};
const ACTION_PLAN_SECTION_ICONS = {
  alert: AlertTriangle,
  check: CheckCircle2,
  database: Database,
  info: Info,
  list: ListChecks,
  search: Search,
  sparkles: Sparkles,
  target: Target,
  trending: TrendingUpDown,
};
const GLOSSARY_TOKEN_PATTERN =
  /(^|[^A-Za-z0-9])(LTCM:CAC|LTCM|nCAC|CAC|AOV|UPT|MER|DR%|GM%|AUR|CCC|Churn|CoD|COGS|CVR|PDP)(?=$|[^A-Za-z0-9])/gi;
const EXACT_GLOSSARY_TOKEN_PATTERN =
  /^(LTCM:CAC|LTCM|nCAC|CAC|AOV|UPT|MER|DR%|GM%|AUR|CCC|Churn|CoD|COGS|CVR|PDP)$/i;

const hasMarkdownTable = (content) =>
  /\n\s*\|?[\s:-]+\|[\s|:-]*\n/.test(content || "");

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const getRevealMode = (content) => {
  if (!content || prefersReducedMotion()) return "none";
  if (hasMarkdownTable(content)) return "fade";
  if (content.includes("\n") && content.length <= MAX_LINE_REVEAL_CHARS) {
    return "line";
  }
  if (content.length <= MAX_TYPEWRITER_CHARS) return "type";
  return "fade";
};

const RevealedAssistantMessageContent = ({
  content,
  isRevealing,
  onRevealComplete,
}) => {
  const messageContent = content || "";
  const revealMode = useMemo(
    () => (isRevealing ? getRevealMode(messageContent) : "none"),
    [messageContent, isRevealing]
  );
  const [visibleContent, setVisibleContent] = useState(() =>
    revealMode === "type" || revealMode === "line" ? "" : messageContent
  );
  const onRevealCompleteRef = useRef(onRevealComplete);

  useEffect(() => {
    onRevealCompleteRef.current = onRevealComplete;
  }, [onRevealComplete]);

  useEffect(() => {
    if (!isRevealing) {
      setVisibleContent(messageContent);
      return undefined;
    }

    if (revealMode === "none" || revealMode === "fade") {
      setVisibleContent(messageContent);
      const timeoutId = window.setTimeout(
        () => onRevealCompleteRef.current?.(),
        180
      );
      return () => {
        window.clearTimeout(timeoutId);
      };
    }

    if (revealMode === "line") {
      const lines = messageContent.split("\n");
      let visibleLineCount = 0;
      setVisibleContent("");

      const intervalId = window.setInterval(() => {
        visibleLineCount += 1;
        setVisibleContent(lines.slice(0, visibleLineCount).join("\n"));

        if (visibleLineCount >= lines.length) {
          window.clearInterval(intervalId);
          window.setTimeout(() => onRevealCompleteRef.current?.(), 0);
        }
      }, LINE_REVEAL_INTERVAL_MS);

      return () => {
        window.clearInterval(intervalId);
      };
    }

    setVisibleContent("");
    const intervalId = window.setInterval(() => {
      setVisibleContent((currentVisibleContent) => {
        const nextVisibleLength = Math.min(
          messageContent.length,
          currentVisibleContent.length + TYPEWRITER_CHARS_PER_TICK
        );

        if (nextVisibleLength >= messageContent.length) {
          window.clearInterval(intervalId);
          window.setTimeout(() => onRevealCompleteRef.current?.(), 0);
        }

        return messageContent.slice(0, nextVisibleLength);
      });
    }, TYPEWRITER_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [messageContent, isRevealing, revealMode]);

  return (
    <div
      className={cn(
        isRevealing &&
          revealMode === "fade" &&
          "animate-in fade-in slide-in-from-bottom-1 duration-200"
      )}
    >
      <AssistantMessageContent content={visibleContent} />
    </div>
  );
};

const PlainUserMessageContent = ({ content }) => (
  <p className="whitespace-pre-wrap break-words leading-relaxed">{content}</p>
);

const compactText = (value, fallback = "") => String(value || fallback).trim();

const formatMessageTimestamp = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
};

const GlossaryText = ({ text, className }) => {
  const value = compactText(text);
  if (!value) return null;

  const parts = value.split(GLOSSARY_TOKEN_PATTERN);
  if (parts.length === 1) return <>{value}</>;

  return (
    <>
      {parts.map((part, index) => {
        if (!part) return null;
        const glossary = EXACT_GLOSSARY_TOKEN_PATTERN.test(part)
          ? getDashboardInsightMetricGlossary(part)
          : "";
        if (!glossary) return <span key={`${part}-${index}`}>{part}</span>;

        return (
          <Tooltip key={`${part}-${index}`}>
            <TooltipTrigger asChild>
              <button
                type="button"
                className={cn(
                  "inline cursor-help rounded-sm border-0 bg-transparent p-0 text-inherit underline decoration-dotted decoration-muted-foreground/35 underline-offset-2 transition-colors hover:decoration-muted-foreground/70 focus-visible:decoration-muted-foreground/80 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary/35",
                  className
                )}
              >
                {part}
              </button>
            </TooltipTrigger>
            <TooltipContent sideOffset={6} className="max-w-72 text-xs">
              {glossary}
            </TooltipContent>
          </Tooltip>
        );
      })}
    </>
  );
};

const getRouteHref = (target) => {
  const routeBuilder = target?.routeName ? routes[target.routeName] : null;
  if (!routeBuilder) return null;

  return routeBuilder(target.routeParams || {});
};

const getInspectTarget = (targetKey) => {
  const target = INSPECT_TARGETS[targetKey];
  if (!target) return null;

  return {
    ...target,
    href: getRouteHref(target),
  };
};

const parseDateOnly = (value) => {
  if (!value || typeof value !== "string") return null;
  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(Date.UTC(year, month - 1, day));
};

const formatDateOnly = (value) => {
  const date = parseDateOnly(value);
  if (!date) return value || "";

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
    timeZone: "UTC",
  }).format(date);
};

const formatDateRange = (from, to) =>
  from && to ? `${formatDateOnly(from)} to ${formatDateOnly(to)}` : "";

const formatWindowLabel = (value) => {
  if (typeof value !== "string") return "";

  const isoRangeMatch = value.match(
    /^(\d{4}-\d{2}-\d{2})\s+to\s+(\d{4}-\d{2}-\d{2})$/
  );
  if (isoRangeMatch) {
    return formatDateRange(isoRangeMatch[1], isoRangeMatch[2]);
  }

  return value;
};

const formatFactWindow = (fact = {}) => {
  if (fact.windowLabel) return formatWindowLabel(fact.windowLabel);
  if (typeof fact.window === "string") return formatWindowLabel(fact.window);
  if (fact.window?.from && fact.window?.to) {
    return formatDateRange(fact.window.from, fact.window.to);
  }

  return "";
};

const joinSummaryParts = (...parts) =>
  parts.map(compactText).filter(Boolean).join(" ");

const InsightFactGrid = ({
  facts = [],
  columns = "sm:grid-cols-2",
  showWindows = true,
}) =>
  facts.length ? (
    <div className={cn("grid gap-2 rounded-xl bg-muted/25 p-3", columns)}>
      {facts.map((fact, index) => {
        const windowLabel = formatFactWindow(fact);

        return (
          <div key={`${fact.key || fact.label}-${index}`} className="min-w-0">
            <div className="break-words text-xs leading-snug text-muted-foreground">
              <GlossaryText text={fact.label} />
            </div>
            <div className="text-sm font-medium">
              <GlossaryText text={fact.value} />
            </div>
            {showWindows && windowLabel ? (
              <div className="mt-0.5 break-words text-[11px] leading-snug text-muted-foreground">
                {windowLabel}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  ) : null;

const MetadataCollapsible = ({
  icon: Icon = null,
  summary,
  children,
  contentClassName,
}) => {
  const [open, setOpen] = useState(false);

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="cursor-pointer grid w-full min-w-0 grid-cols-[1.25rem_minmax(0,1fr)_auto] items-center gap-2 rounded-md py-1 text-left text-xs text-muted-foreground transition-colors hover:bg-muted/30 hover:text-foreground"
        >
          <span className="flex size-5 items-center justify-center">
            {Icon ? <Icon className="size-3.5 shrink-0" /> : null}
          </span>
          <span className="min-w-0 truncate">{summary}</span>
          <ChevronDown
            className={cn(
              "size-3 shrink-0 transition-transform",
              open && "rotate-180"
            )}
          />
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className={cn("pb-1 pl-1 pt-0.5", contentClassName)}>
        {children}
      </CollapsibleContent>
    </Collapsible>
  );
};

const CompactFactList = ({ facts = [] }) => (
  <div className="grid gap-2 sm:grid-cols-2">
    {facts.map((fact, index) => {
      const windowLabel = formatFactWindow(fact);

      return (
        <div key={`${fact.label}-${index}`} className="min-w-0">
          <div className="break-words text-xs leading-snug text-muted-foreground">
            <GlossaryText text={fact.label} />
          </div>
          <div className="text-sm font-medium text-foreground">
            <GlossaryText text={fact.value} />
          </div>
          {windowLabel ? (
            <div className="mt-0.5 break-words text-[11px] leading-snug text-muted-foreground">
              {windowLabel}
            </div>
          ) : null}
        </div>
      );
    })}
  </div>
);

const InsightRequestCard = ({ payload, fallbackContent }) => {
  const title = compactText(payload?.title, "Dashboard insight");
  const summary = compactText(payload?.summary);
  const facts = Array.isArray(payload?.facts) ? payload.facts : [];
  const topFacts = facts.slice(0, 3);
  const sourceSummary = compactText(payload?.sourceSummary, "Store data");
  const factsSummary = joinSummaryParts("Audit facts used:", sourceSummary);

  if (!payload || typeof payload !== "object") {
    return <PlainUserMessageContent content={fallbackContent} />;
  }

  return (
    <div className="space-y-3 rounded-2xl bg-card/95 p-4 text-foreground shadow-sm">
      <div className="flex min-w-0 items-center gap-2">
        <div className="flex size-8 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <Sparkles className="size-4" />
        </div>
        <div className="min-w-0">
          <div className="text-xs font-semibold uppercase tracking-wide text-primary">
            Insight action plan
          </div>
          <div className="break-words text-sm font-semibold leading-snug">
            {title}
          </div>
        </div>
      </div>

      {summary ? (
        <p className="text-sm leading-relaxed text-muted-foreground">
          {summary}
        </p>
      ) : null}

      {topFacts.length ? (
        <InsightFactGrid
          facts={topFacts}
          columns="sm:grid-cols-3"
          showWindows={false}
        />
      ) : null}

      {facts.length ? (
        <MetadataCollapsible icon={Database} summary={factsSummary}>
          <CompactFactList facts={facts} />
        </MetadataCollapsible>
      ) : null}
    </div>
  );
};

const legacyActionPlanSectionsFromPayload = (payload = {}) => {
  const sections = [];
  const priorityActions = Array.isArray(payload.priorityActions)
    ? payload.priorityActions
    : [];
  const inspectNext = Array.isArray(payload.inspectNext)
    ? payload.inspectNext
    : [];
  const decisionTriggers = Array.isArray(payload.decisionTriggers)
    ? payload.decisionTriggers
    : [];

  if (priorityActions.length) {
    sections.push({
      id: "priority-moves",
      title: "Priority moves",
      summary: "Highest-leverage next actions",
      iconKey: "target",
      prominence: "primary",
      blocks: [{ type: "actions", actions: priorityActions }],
    });
  }
  if (inspectNext.length) {
    sections.push({
      id: "inspect-next",
      title: "Inspect next",
      summary: "Useful follow-up views",
      iconKey: "search",
      prominence: "supporting",
      blocks: [{ type: "inspection_links", inspectionLinks: inspectNext }],
    });
  }
  if (decisionTriggers.length) {
    sections.push({
      id: "decision-triggers",
      title: "What would change the recommendation",
      summary: "Signals that should change the next move",
      iconKey: "alert",
      prominence: "supporting",
      blocks: [{ type: "conditions", conditions: decisionTriggers }],
    });
  }

  return sections;
};

const getActionPlanSections = (payload = {}) =>
  Array.isArray(payload.sections) && payload.sections.length
    ? payload.sections
    : legacyActionPlanSectionsFromPayload(payload);

const getActionPlanSectionIcon = (section = {}) =>
  ACTION_PLAN_SECTION_ICONS[section.iconKey] || Info;

const ActionPlanActionsBlock = ({ actions = [] }) =>
  actions.length ? (
    <div className="space-y-2">
      {actions.map((action, index) => (
        <div
          key={`${action.title}-${index}`}
          className="rounded-lg bg-secondary p-3 transition-colors dark:bg-background/40"
        >
          <div className="flex items-start gap-2">
            <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-primary/10 dark:bg-primary/50 text-[11px] font-semibold text-primary dark:text-foreground">
              {index + 1}
            </span>
            <div className="min-w-0 space-y-1">
              <div className="text-sm font-semibold leading-snug">
                <GlossaryText text={action.title} />
              </div>
              {action.detail ? (
                <p className="text-sm leading-relaxed text-muted-foreground">
                  <GlossaryText text={action.detail} />
                </p>
              ) : null}
              {action.firstStep ? (
                <p className="text-xs leading-relaxed text-foreground">
                  <span className="font-medium">First: </span>
                  <GlossaryText text={action.firstStep} />
                </p>
              ) : null}
            </div>
          </div>
        </div>
      ))}
    </div>
  ) : null;

const ActionPlanInspectionLinksBlock = ({ inspectionLinks = [] }) =>
  inspectionLinks.length ? (
    <div className="grid gap-2 sm:grid-cols-2">
      {inspectionLinks.map((item, index) => {
        const inspectTarget = getInspectTarget(item.target);

        return (
          <div
            key={`${item.label}-${index}`}
            className="rounded-lg bg-secondary p-3 transition-colors dark:bg-background/40"
          >
            <div className="text-sm font-semibold">
              <GlossaryText text={item.label} />
            </div>
            {item.reason ? (
              <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                <GlossaryText text={item.reason} />
              </p>
            ) : null}
            {inspectTarget?.href ? (
              <Link
                to={inspectTarget.href}
                className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
              >
                {item.linkLabel || inspectTarget.label}
                <ArrowRight className="size-3" />
              </Link>
            ) : null}
          </div>
        );
      })}
    </div>
  ) : null;

const ActionPlanConditionsBlock = ({ conditions = [] }) =>
  conditions.length ? (
    <div className="space-y-2">
      {conditions.map((condition, index) => (
        <div
          key={`${condition.label}-${index}`}
          className="rounded-lg bg-secondary dark:bg-background/40 p-3"
        >
          <div className="text-sm font-semibold">
            <GlossaryText text={condition.label} />
          </div>
          <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
            <GlossaryText text={condition.condition} />
          </p>
        </div>
      ))}
    </div>
  ) : null;

const ActionPlanKeyValuesBlock = ({ rows = [] }) =>
  rows.length ? (
    <div className="grid gap-2 sm:grid-cols-2">
      {rows.map((row, index) => (
        <div key={`${row.label}-${index}`} className="min-w-0">
          <div className="break-words text-xs leading-snug text-muted-foreground">
            <GlossaryText text={row.label} />
          </div>
          <div className="text-sm font-medium text-foreground">
            <GlossaryText text={row.value} />
          </div>
        </div>
      ))}
    </div>
  ) : null;

const ActionPlanSectionBlock = ({ block }) => {
  if (!block || typeof block !== "object") return null;

  if (block.type === "text") {
    return block.text ? (
      <p className="text-sm leading-relaxed text-muted-foreground">
        <GlossaryText text={block.text} />
      </p>
    ) : null;
  }
  if (block.type === "bullets") {
    const items = Array.isArray(block.items) ? block.items : [];
    return items.length ? (
      <ul className="space-y-1 pl-4 text-sm leading-relaxed text-muted-foreground">
        {items.map((item, index) => (
          <li key={`${item}-${index}`} className="list-disc">
            <GlossaryText text={item} />
          </li>
        ))}
      </ul>
    ) : null;
  }
  if (block.type === "actions") {
    return <ActionPlanActionsBlock actions={block.actions || []} />;
  }
  if (block.type === "inspection_links") {
    return (
      <ActionPlanInspectionLinksBlock
        inspectionLinks={block.inspectionLinks || []}
      />
    );
  }
  if (block.type === "conditions") {
    return <ActionPlanConditionsBlock conditions={block.conditions || []} />;
  }
  if (block.type === "key_values") {
    return <ActionPlanKeyValuesBlock rows={block.rows || []} />;
  }

  return null;
};

const ActionPlanSection = ({ section }) => {
  const Icon = getActionPlanSectionIcon(section);
  const blocks = Array.isArray(section.blocks) ? section.blocks : [];
  const isPrimary = section.prominence === "primary";
  const content = (
    <div className="space-y-3">
      {blocks.map((block, index) => (
        <ActionPlanSectionBlock key={`${block.type}-${index}`} block={block} />
      ))}
    </div>
  );

  if (isPrimary) {
    return (
      <section className="space-y-2 rounded-xl bg-muted/15">
        <div className="grid min-w-0 grid-cols-[1.25rem_minmax(0,1fr)] gap-2">
          <span className="flex size-5 items-center justify-center">
            <Icon className="size-4 shrink-0 text-primary" />
          </span>
          <div className="min-w-0">
            <div className="text-sm font-semibold leading-snug">
              {section.title}
            </div>
            {section.summary ? (
              <div className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
                <GlossaryText text={section.summary} />
              </div>
            ) : null}
          </div>
        </div>
        <div className="pl-1">{content}</div>
      </section>
    );
  }

  return (
    <MetadataCollapsible
      icon={Icon}
      summary={`${section.title}: ${section.summary || "Details"}`}
    >
      {content}
    </MetadataCollapsible>
  );
};

const InsightActionPlanCard = ({ payload, fallbackContent }) => {
  if (!payload || typeof payload !== "object") {
    return <AssistantMessageContent content={fallbackContent} />;
  }

  const title = compactText(payload.title, "Action plan");
  const summary = compactText(payload.summary);
  const sections = getActionPlanSections(payload);
  const factsUsed = Array.isArray(payload.factsUsed) ? payload.factsUsed : [];

  return (
    <div className="space-y-3 rounded-2xl bg-card/95 p-4 text-foreground shadow-sm sm:p-5">
      <div className="space-y-1.5">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-primary">
          Practical action plan
        </div>
        <h3 className="text-base font-semibold leading-snug">{title}</h3>
        {summary ? (
          <p className="text-sm leading-relaxed text-muted-foreground">
            <GlossaryText text={summary} />
          </p>
        ) : null}
      </div>

      <div className="space-y-2">
        {sections.map((section, index) => (
          <ActionPlanSection
            key={section.id || `${section.title}-${index}`}
            section={section}
          />
        ))}

        {factsUsed.length ? (
          <MetadataCollapsible icon={Database} summary="Facts used">
            <CompactFactList facts={factsUsed} />
          </MetadataCollapsible>
        ) : null}
      </div>
    </div>
  );
};

const InstantReveal = ({ messageId, onRevealComplete, children }) => {
  useEffect(() => {
    if (messageId) {
      onRevealComplete?.(messageId);
    }
  }, [messageId, onRevealComplete]);

  return children;
};

export const AssistantConversation = ({
  scrollAreaRef,
  isLoadingState,
  messages,
  showEmptyState,
  suggestions,
  isComposerDisabled,
  isSelectedThreadRunning,
  runProgressMessage,
  revealingMessageIds,
  onSelectPrompt,
  onMessageRevealComplete,
}) => (
  <div ref={scrollAreaRef} className="min-h-0 flex-1 overflow-hidden">
    <ScrollArea className="h-full">
      <div className="space-y-5 p-4 sm:p-5">
        {isLoadingState ? (
          <div
            className={cn(
              "text-muted-foreground flex items-center gap-2 text-sm",
              messages.length === 0 ? "mt-8 justify-center" : "mr-auto"
            )}
          >
            <Loader2 className="size-4 animate-spin" />
            Loading chat...
          </div>
        ) : null}

        {showEmptyState ? (
          <div className="mx-auto mt-12 flex max-w-xl flex-col items-center gap-5 text-center">
            <div className="bg-primary/10 text-primary flex size-12 items-center justify-center rounded-2xl">
              <Sparkles className="size-5" />
            </div>
            <div className="space-y-2">
              <p className="text-xl font-semibold tracking-tight">
                Ask questions about your store data
              </p>
              <p className="text-muted-foreground text-sm">
                Start fresh, or open a saved chat from history.
              </p>
            </div>
            <PromptSuggestions
              suggestions={suggestions}
              onSelect={onSelectPrompt}
              disabled={isComposerDisabled}
            />
          </div>
        ) : null}

        {messages.map((message, index) => {
          const isInsightRequest =
            message.role === "user" &&
            message.displayKind === INSIGHT_ACTION_PLAN_KIND;
          const isInsightActionPlanResponse =
            message.role === "assistant" &&
            message.displayKind === INSIGHT_ACTION_PLAN_RESPONSE_KIND;
          const timestampLabel = formatMessageTimestamp(message.createdAt);

          return (
            <div
              key={
                message.id ||
                `${message.role}-${index}-${message.content.slice(0, 16)}`
              }
              data-assistant-message-id={message.id || undefined}
              data-assistant-message-role={message.role}
              aria-label={
                timestampLabel ? `Message sent ${timestampLabel}` : undefined
              }
              className={cn(
                "group/message text-sm",
                isInsightRequest
                  ? "ml-auto max-w-[min(38rem,94%)]"
                  : isInsightActionPlanResponse
                    ? "mr-auto max-w-[min(46rem,96%)]"
                    : "max-w-[88%] rounded-2xl px-4 py-3 shadow-sm",
                !isInsightRequest &&
                  !isInsightActionPlanResponse &&
                  (message.role === "user"
                    ? "bg-primary text-primary-foreground ml-auto"
                    : "bg-card/95 border-border/80 mr-auto border")
              )}
            >
              {message.role === "assistant" ? (
                isInsightActionPlanResponse ? (
                  <InstantReveal
                    messageId={message.id}
                    onRevealComplete={onMessageRevealComplete}
                  >
                    <InsightActionPlanCard
                      payload={message.displayPayload}
                      fallbackContent={message.content}
                    />
                  </InstantReveal>
                ) : (
                  <RevealedAssistantMessageContent
                    content={message.content}
                    isRevealing={Boolean(
                      message.id && revealingMessageIds?.has(message.id)
                    )}
                    onRevealComplete={() =>
                      onMessageRevealComplete?.(message.id)
                    }
                  />
                )
              ) : isInsightRequest ? (
                <InsightRequestCard
                  payload={message.displayPayload}
                  fallbackContent={message.content}
                />
              ) : (
                <PlainUserMessageContent content={message.content} />
              )}
              {timestampLabel ? (
                <div
                  className={cn(
                    "mt-1.5 text-[11px] leading-none text-muted-foreground/70 opacity-0 transition-opacity group-hover/message:opacity-100 group-focus-within/message:opacity-100",
                    message.role === "user"
                      ? "text-right text-primary-foreground/70"
                      : "text-left"
                  )}
                >
                  {timestampLabel}
                </div>
              ) : null}
            </div>
          );
        })}

        {isSelectedThreadRunning ? (
          <div className="text-muted-foreground mr-auto flex items-center gap-2 text-sm">
            <Loader2 className="size-4 animate-spin" />
            {runProgressMessage || DEFAULT_PROGRESS_MESSAGE}
          </div>
        ) : null}
      </div>
    </ScrollArea>
  </div>
);
